import { GeneratedBook } from "../types";

export interface ParentInsights {
  topThemes: Array<{ label: string; count: number }>;
  topTones: Array<{ label: string; count: number }>;
  topValues: Array<{ label: string; count: number }>;
  whyGenerated: string;
  emphasizedValue: string;
}

const VALUE_KEYWORDS: Record<string, string[]> = {
  Kindness: ["kind", "kindness", "help", "share", "gentle", "care"],
  Courage: ["courage", "brave", "fear", "bold", "stand up", "try again"],
  Patience: ["patient", "wait", "slow", "calm", "take turns"],
  Honesty: ["honest", "truth", "admit", "apologize", "promise"],
  Gratitude: ["grateful", "thank", "gratitude", "appreciate"],
  Curiosity: ["curious", "question", "discover", "explore", "wonder"],
};

const TONE_KEYWORDS: Record<string, string[]> = {
  Calm: ["calm", "quiet", "soft", "peaceful", "bedtime"],
  Adventurous: ["adventure", "journey", "quest", "explore", "discover"],
  Funny: ["funny", "laugh", "giggle", "silly", "joke"],
  Sweet: ["sweet", "warm", "love", "cozy", "gentle"],
  Whimsical: ["magic", "sparkle", "wonder", "fantasy", "dream"],
  Mysterious: ["mystery", "secret", "hidden", "clue", "unknown"],
};

function inc(map: Map<string, number>, key: string) {
  map.set(key, (map.get(key) || 0) + 1);
}

function topN(map: Map<string, number>, n = 3): Array<{ label: string; count: number }> {
  return [...map.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([label, count]) => ({ label, count }));
}

function collectStoryText(story: GeneratedBook): string {
  const pageText = (story.pages || []).map((p) => p.text || "").join(" ");
  return `${story.title || ""} ${story.synopsis || ""} ${pageText}`.toLowerCase();
}

function detectKeywordMatches(text: string, dictionary: Record<string, string[]>): Map<string, number> {
  const score = new Map<string, number>();
  Object.entries(dictionary).forEach(([label, words]) => {
    const count = words.reduce((acc, keyword) => {
      const matches = text.split(keyword.toLowerCase()).length - 1;
      return acc + (matches > 0 ? matches : 0);
    }, 0);
    if (count > 0) {
      score.set(label, count);
    }
  });
  return score;
}

export function buildParentInsights(stories: GeneratedBook[], childName?: string): ParentInsights {
  const themeCounts = new Map<string, number>();
  const toneCounts = new Map<string, number>();
  const valueCounts = new Map<string, number>();

  stories.forEach((story) => {
    (story.tags || []).forEach((tag) => {
      const clean = (tag || "").trim();
      if (clean) inc(themeCounts, clean);
    });

    const storyText = collectStoryText(story);
    const tones = detectKeywordMatches(storyText, TONE_KEYWORDS);
    tones.forEach((count, label) => toneCounts.set(label, (toneCounts.get(label) || 0) + count));

    const values = detectKeywordMatches(storyText, VALUE_KEYWORDS);
    values.forEach((count, label) => valueCounts.set(label, (valueCounts.get(label) || 0) + count));
  });

  const topThemes = topN(themeCounts);
  const topTones = topN(toneCounts);
  const topValues = topN(valueCounts);

  const latestStory = stories[0];
  const whyGenerated = latestStory
    ? `Built from recent reading preferences${childName ? ` for ${childName}` : ""}, with emphasis on ${topThemes[0]?.label || "age-appropriate themes"} and a ${topTones[0]?.label?.toLowerCase() || "balanced"} tone.`
    : "Generate a story to see explainability insights here.";

  const emphasizedValue = topValues[0]?.label || "Not enough signal yet";

  return {
    topThemes,
    topTones,
    topValues,
    whyGenerated,
    emphasizedValue,
  };
}
